import React from 'react';
import {
  Card,
  CardHeader,
  Divider,
  CardContent,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  Collapse,
  Paper,
  CardActions,
  ListItemButton,
  TextField,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Alert,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import EditOutlined from '@mui/icons-material/EditOutlined';
import {
  ChevronLeft,
  KeyboardArrowUp,
  KeyboardArrowDown,
  DeleteOutline,
  PlaceOutlined,
  Add,
  Close,
  CheckCircleRounded,
  CloseOutlined,
  ErrorOutline,
  InfoOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { createPortal } from 'react-dom';

import { i18next } from '@/config/i18n';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'underscore';

import settingsApi from '@/apis/settingsApi';
import { RootState } from '@/index';
import { setSelectedTerritory } from '@/actions/territories/pure';
import {
  getTerritories,
  getTerritoryById,
  createTerritoryFromPostalCodes,
  addPostalCodesToTerritoryFromInput,
  deletePostalCodesFromTerritory,
  renameTerritoryName,
} from '@/actions/territories/thunks';

/* ===================== STYLES ===================== */
const useStyles = makeStyles((theme: any) => ({
  zipPaper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    overflow: 'hidden',
  },
  zipList: {
    padding: 0,
    maxHeight: 360,
    overflowY: 'auto',
  },
  zipListItem: {
    padding: 0,
  },
  zipListItemButton: {
    padding: theme.spacing(1.5, 2),
    minHeight: 56,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  zipIcon: {
    minWidth: 40,
    color: theme.palette.text.secondary,
  },
  zipText: {
    marginRight: theme.spacing(6),
    fontSize: '14px',
  },
  zipSecondaryAction: {
    right: theme.spacing(2),
  },
  zipCheckbox: {
    padding: 0,
  },
  dialogPaper: {
    borderRadius: 6,
    width: 460,
    maxWidth: '90%',
  },
  titleRoot: {
    padding: '16px 12px 12px 16px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  iconWrap: {
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  },
  deleteIcon: {
    color: '#ef5927',
  },
  titleText: {
    fontSize: 20,
    fontWeight: 600,
    lineHeight: '24px',
    color: theme?.palette?.text?.primary || '#111',
    flex: 1,
  },
  closeBtn: {
    padding: 6,
  },
  messageText: {
    fontSize: 14,
    lineHeight: '20px',
    color: theme?.palette?.text?.secondary || '#6b6b6b',
  },
  actionsRoot: {
    padding: '0 16px 16px 16px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
  },
  cancelBtn: {
    textTransform: 'uppercase',
    fontWeight: 600,
    borderWidth: 1,
    padding: '8px 14px',
  },
  deleteBtn: {
    textTransform: 'uppercase',
    fontWeight: 700,
    padding: '8px 18px',
    backgroundColor: '#ef5927',
    color: '#fff !important',
    boxShadow: 'none',

    '&:hover': {
      backgroundColor: '#ef5927',
      color: '#fff',
    },
  },
}));

/* ===================== TYPES ===================== */
type PostalCode = {
  id: string;
  country?: string;
  code?: string;
};

type PendingPostalCode = {
  tempId: string;
  country: string;
  code: string;
};

function TerritoryDetail() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { selected_territory } = useSelector((s: RootState) => s.territories, isEqual);

  // UI states
  const [name, setName] = React.useState('');
  //const [identifier, setIdentifier] = React.useState('');
  const [newZip, setNewZip] = React.useState('');
  const [country, setCountry] = React.useState('DE');
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [collapsed, setCollapse] = React.useState(true);
  const [selectedCodes, setSelectedCodes] = React.useState<string[]>([]);
  const [deleting, setDeleting] = React.useState(false);

  // validation/loading
  const [zipValidating, setZipValidating] = React.useState(false);

  // pending (shown in SAME list)
  const [pendingPostalCodes, setPendingPostalCodes] = React.useState<PendingPostalCode[]>([]);

  // dialogs
  const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');

  const [renameOpen, setRenameOpen] = React.useState(false);
  const [renameValue, setRenameValue] = React.useState('');
  const [renaming, setRenaming] = React.useState(false);

  // toast
  const [toast, setToast] = React.useState<{
    open: boolean;
    msg: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, msg: '', severity: 'success' });

  if (!selected_territory) return null;

  // sync when territory changes
  React.useEffect(() => {
    const t: any = selected_territory as any;

    setName(t?.name ?? i18next.t('common:areaEditor.territoryName'));
    setRenameValue(t?.name ?? i18next.t('common:areaEditor.territoryName'));
    //setIdentifier(t?.identifier ?? '');

    setSelectedCodes([]);
    setNewZip('');
    setCountry('DE');
    setPendingPostalCodes([]);
  }, [selected_territory?.id]);

  const title =
    name?.trim() || (selected_territory as any)?.name || i18next.t('common:areaEditor.territoryName');

  const postalCodes: PostalCode[] = Array.isArray((selected_territory as any)?.postal_codes)
    ? (selected_territory as any).postal_codes
    : [];

  // merge existing + pending in SAME list
  const pendingDisplayPostalCodes: PostalCode[] = pendingPostalCodes.map((p) => ({
    id: p.tempId,
    country: p.country,
    code: p.code,
  }));

  const displayPostalCodes: PostalCode[] = [...postalCodes, ...pendingDisplayPostalCodes];
  const postalCount = displayPostalCodes.length;

  const toggleZip = (zipId: string) => {
    setSelectedCodes((prev) => (prev.includes(zipId) ? prev.filter((x) => x !== zipId) : [...prev, zipId]));
  };

  const clearSelected = () => setSelectedCodes([]);

  const parsePostalConflict = (msg: string) => {
    const m = msg.match(/Postal code\(s\)\s+'([^']+)'\s+already exist in territory\s+'([^']+)'/i);
    if (!m) return null;
    return { postal_code: m[1], territory_name: m[2] };
  };

  const showConflictToast = (backendMsg: string) => {
    const conflict = parsePostalConflict(backendMsg);

    if (conflict) {
      setToast({
        open: true,
        msg: i18next.t('common:areaEditor.postalCodeExistsInOtherTerritory', {
          postal_code: conflict.postal_code,
          territory_name: conflict.territory_name,
          // safety fallback
          defaultValue: `Postal code ${conflict.postal_code} already exists in territory ${conflict.territory_name}`,
        }),
        severity: 'error',
      });
      return;
    }

    // fallback agar format change ho jaye
    setToast({
      open: true,
      msg: i18next.t('common:errors.generic', {
        defaultValue: backendMsg,
      }),
      severity: 'error',
    });
  };


  const handleDeleteSelectedPostalCodes = async () => {
    if (!selectedCodes.length || deleting) return;
    setConfirmOpen(true);
  };

  const handleConfirmDeletePostalCodes = async () => {
    setConfirmOpen(false);

    try {
      setDeleting(true);

      const pendingToRemove = selectedCodes.filter((id) => id.startsWith('pending_'));
      const existingToDelete = selectedCodes.filter((id) => !id.startsWith('pending_'));

      // remove pending locally
      if (pendingToRemove.length) {
        setPendingPostalCodes((prev) => prev.filter((p) => !pendingToRemove.includes(p.tempId)));
      }

      // delete existing from backend
      if (existingToDelete.length) {
        await dispatch(deletePostalCodesFromTerritory(selected_territory.id, existingToDelete) as any);
        await dispatch(getTerritoryById(selected_territory.id) as any);
      }

      clearSelected();
    } catch (err) {
      console.error('Failed to delete postal codes:', err);
    } finally {
      setDeleting(false);
    }
  };

  const PAGE_SIZE = 100;

  const handleSave = async () => {
    try {
      if (!name.trim()) return;

      const isNew = selected_territory.id.startsWith('tmp_');

      // convert pending for thunks
      const inputPostalCodes = pendingPostalCodes.map((p) => ({ country: p.country, code: p.code }));

      if (isNew) {
        await dispatch(createTerritoryFromPostalCodes(name, inputPostalCodes) as any);
        await dispatch(getTerritories(false, PAGE_SIZE) as any);
        dispatch(setSelectedTerritory(undefined));
        return;
      }

      if (inputPostalCodes.length) {
        await dispatch(addPostalCodesToTerritoryFromInput(selected_territory.id, inputPostalCodes) as any);
        await dispatch(getTerritoryById(selected_territory.id) as any);
        setPendingPostalCodes([]);
      }
    } catch (err: any) {
      const details = err?.response?.data?.detail;
      const backendMsg =
        (Array.isArray(details) && details.length && details[0]?.msg) ||
        err?.response?.data?.msg ||
        err?.response?.data?.message ||
        'Failed to save territory';

      // ✅ If it is postal conflict, show TOAST only (no modal)
      if (typeof backendMsg === 'string' && backendMsg.toLowerCase().includes('already exist in territory')) {
        showConflictToast(backendMsg);
        return;
      }

      setErrorMessage(backendMsg);
      setErrorDialogOpen(true);
    }
  };

  const handleAddZip = async () => {
    const code = newZip.trim();
    if (!code || zipValidating) return;

    const isNew = selected_territory.id.startsWith('tmp_');

    try {
      setZipValidating(true);

      // 1) Duplicate check in current UI (pending + existing list)
      const existsInPending = pendingPostalCodes.some((p) => p.country === country && p.code === code);
      const existsInExisting = postalCodes.some((p) => p.country === country && p.code === code);

      if (existsInPending || existsInExisting) {
        setToast({
          open: true,
          msg: i18next.t('common:areaEditor.postalCodeAlreadyExists', {
            postal_code: `${country}-${code}`,
            territory_name: title,
            defaultValue: `Postal code(s) '${country}-${code}' already exist in territory '${title}'`,
          }),
          severity: 'error',
        });
        setNewZip('');
        return;
      }

      // 2) Lookup => validate + get id
      const res = await settingsApi.getPostalCodesByInput({
        postal_codes: [{ country, code }],
      });

      const first = res?.data?.results?.[0];
      const postalId = first?.id;

      // invalid postal code
      if (!postalId) {
        setToast({
          open: true,
          msg: i18next.t('common:areaEditor.invalidPostalCode', {
            postal_code: `${country}-${code}`,
            defaultValue: `Postal code "${country}-${code}" is not valid. Please enter a valid postal code.`,
          }),
          severity: 'error',
        });
        return;
      }

      // 3) New territory -> keep local pending only
      if (isNew) {
        const tempId = `pending_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        setPendingPostalCodes((prev) => [...prev, { tempId, country, code }]);
        setNewZip('');
        return;
      }

      // 4) Existing territory -> assign immediately to get backend conflict on '+'
      try {
        // expects body: ["uuid"]
        await settingsApi.assignPostalCodeIds(selected_territory.id, [postalId]);
        await dispatch(getTerritoryById(selected_territory.id) as any);
        setNewZip('');
      } catch (err: any) {
        const details = err?.response?.data?.detail;
        const backendMsg =
          (Array.isArray(details) && details.length && details[0]?.msg) ||
          err?.response?.data?.msg ||
          err?.response?.data?.message ||
          'Failed to assign postal code';

        // ✅ show i18n toast (parsed)
        if (typeof backendMsg === 'string') showConflictToast(backendMsg);
      }
    } catch (err) {
      setToast({
        open: true,
        msg: i18next.t('common:areaEditor.postalCodeValidationFailed', {
          defaultValue: 'Could not validate postal code. Please try again.',
        }),
        severity: 'error',
      });
    } finally {
      setZipValidating(false);
    }
  };

  const CustomToast: React.FC<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
    autoHideMs?: number;
    onClose: () => void;
  }> = ({ open, message, severity, autoHideMs = 2500, onClose }) => {
    React.useEffect(() => {
      if (!open) return;
      const t = setTimeout(onClose, autoHideMs);
      return () => clearTimeout(t);
    }, [open, autoHideMs, onClose]);

    if (!open) return null;

    return createPortal(
      <Box
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 24,
          transform: 'translateX(-50%)',
          zIndex: 1400,
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ pointerEvents: 'auto' }}>
          <Alert
            severity={severity}
            variant="filled"
            onClose={onClose}
            iconMapping={{
              success: <CheckCircleRounded fontSize="small" />,
              error: <ErrorOutline fontSize="small" />,
              info: <InfoOutlined fontSize="small" />,
              warning: <WarningAmberOutlined fontSize="small" />,
            }}
            action={
              <IconButton size="small" onClick={onClose}>
                <CloseOutlined fontSize="small" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Box>
      </Box>,
      document.body
    );
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Card raised style={{ height: window.innerHeight - 93, overflowY: 'auto' }}>
          {/* TOP HEADER */}
          <CardHeader
            avatar={
              <IconButton onClick={() => dispatch(setSelectedTerritory(undefined))} size="large">
                <ChevronLeft fontSize="large" />
              </IconButton>
            }
            title={
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  '& .edit-btn': {
                    opacity: 0,
                    transition: 'opacity 120ms ease',
                  },
                  '&:hover .edit-btn': {
                    opacity: 1,
                  },
                }}
              >
                <Typography variant="h5">
                  {title}
                </Typography>

                <IconButton
                  size="small"
                  className="edit-btn"
                  onClick={() => {
                    setRenameValue(name?.trim() ? name : title);
                    setRenameOpen(true);
                  }}
                  aria-label="edit territory name"
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Box>
            }
          />
          <Divider />

          <CardContent>
            {/* POSTAL CODES */}
            <Card variant="outlined">
              <CardHeader
                title={
                  selectedCodes.length ? (
                    <Typography color="textSecondary">
                      {`${selectedCodes.length} ${i18next.t('common:areaEditor.selected')}`}
                    </Typography>
                  ) : (
                    `${postalCount} ${i18next.t('common:areaEditor.postalcodes', {
                      multi: postalCount !== 1 ? (i18next.language === 'de' ? 'en' : 's') : '',
                    })}`
                  )
                }
                titleTypographyProps={{ variant: 'h6', color: 'primary' }}
                action={
                  selectedCodes.length ? (
                    <IconButton onClick={handleDeleteSelectedPostalCodes} size="large" disabled={deleting}>
                      <DeleteOutline />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => setCollapse(!collapsed)} size="large">
                      {collapsed ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                  )
                }
              />
              <Divider />

              <Collapse in={collapsed}>
                <CardContent>
                  {displayPostalCodes.length > 0 && (
                    <Paper elevation={0} className={classes.zipPaper}>
                      <List dense className={classes.zipList}>
                        {displayPostalCodes.map((zip, i) => {
                          const checked = selectedCodes.includes(zip.id);

                          return (
                            <ListItem key={`${zip.id}_${i}`} disablePadding className={classes.zipListItem}>
                              <ListItemButton className={classes.zipListItemButton}>
                                <ListItemIcon className={classes.zipIcon}>
                                  <PlaceOutlined />
                                </ListItemIcon>

                                <ListItemText
                                  className={classes.zipText}
                                  primary={`${zip.code ?? ''} ${zip.country ?? ''}`.trim()}
                                />

                                <ListItemSecondaryAction className={classes.zipSecondaryAction}>
                                  <Checkbox
                                    className={classes.zipCheckbox}
                                    checked={checked}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleZip(zip.id);
                                    }}
                                  />
                                </ListItemSecondaryAction>
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                  )}
                </CardContent>

                <CardActions sx={{ padding: 2 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs>
                      <TextField
                        fullWidth
                        size="medium"
                        placeholder="PLZ zuweisen"
                        value={newZip}
                        onChange={(e) => setNewZip(e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={handleAddZip} disabled={zipValidating}>
                                <Add />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item sx={{ minWidth: 90 }}>
                      <TextField
                        select
                        size="medium"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        label="Land *"
                      >
                        <MenuItem value="DE">DE</MenuItem>
                        <MenuItem value="AT">AT</MenuItem>
                        <MenuItem value="CH">CH</MenuItem>
                        <MenuItem value="FR">FR</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </CardActions>
              </Collapse>
            </Card>

            {/* SAVE BAR */}
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                backgroundColor: '#fff',
                borderTop: '1px solid #e0e0e0',
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                zIndex: 10,
              }}
            >
              <Button variant="contained" onClick={handleSave} disabled={!name.trim()}>
                {i18next.t('common:buttons.Save', { defaultValue: 'Save' })}
              </Button>
            </Box>

            {/* CONFIRM DELETE DIALOG */}
            <Dialog
              open={confirmOpen}
              onClose={() => setConfirmOpen(false)}
              maxWidth="xs"
              fullWidth={false}
              classes={{ paper: classes.dialogPaper }}
            >
              <DialogTitle className={classes.titleRoot}>
                <div className={classes.titleRow}>
                  <div className={classes.iconWrap}>
                    <DeleteOutline fontSize="medium" className={classes.deleteIcon} />
                  </div>

                  <div className={classes.titleText}>{i18next.t('common:areaEditor.deleteTerritory')}</div>

                  <IconButton className={classes.closeBtn} onClick={() => setConfirmOpen(false)} size="small">
                    <Close />
                  </IconButton>
                </div>
              </DialogTitle>

              <DialogContent>
                <Typography className={classes.messageText}>
                  {i18next.t('common:areaEditor.deletePostalMsg')}
                </Typography>
              </DialogContent>

              <DialogActions className={classes.actionsRoot}>
                <Button onClick={() => setConfirmOpen(false)} variant="outlined" className={classes.cancelBtn}>
                  {i18next.t('common:buttons.cancel')}
                </Button>

                <Button
                  onClick={handleConfirmDeletePostalCodes}
                  variant="contained"
                  className={classes.deleteBtn}
                  disabled={deleting}
                  autoFocus
                >
                  {i18next.t('common:buttons.delete')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* ERROR DIALOG (only for non-conflict errors) */}
            <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Territory Save Failed</DialogTitle>
              <DialogContent>
                <Typography color="error" sx={{ whiteSpace: 'pre-line' }}>
                  {errorMessage}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setErrorDialogOpen(false)} variant="contained" autoFocus>
                  OK
                </Button>
              </DialogActions>
            </Dialog>

            {/* RENAME DIALOG */}
            <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pr: 1,
                }}
              >
                {i18next.t('common:areaEditor.UpdateTerritory')}

                <IconButton
                  onClick={() => setRenameOpen(false)}
                  size="small"
                  aria-label="close"
                >
                  <Close />
                </IconButton>
              </DialogTitle>

              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  margin="dense"
                  label={i18next.t('common:areaEditor.newTerritoryName')}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  disabled={renaming}
                />
              </DialogContent>

              <DialogActions>
                <Button onClick={() => setRenameOpen(false)} variant="outlined" disabled={renaming}>
                     {i18next.t('common:buttons.cancel')}
                </Button>

                <Button
                  onClick={async () => {
                    if (!renameValue.trim()) return;

                    const isNew = selected_territory.id.startsWith('tmp_');

                    try {
                      setRenaming(true);

                      // Existing territory → backend rename
                      if (!isNew) {
                        await dispatch(
                          renameTerritoryName(selected_territory.id, renameValue.trim()) as any
                        );
                      }

                      // New (tmp_) OR existing → update local name
                      setName(renameValue.trim());
                      setRenameOpen(false);
                    } catch (err: any) {
                      const details = err?.response?.data?.detail;
                      const backendMsg =
                        (Array.isArray(details) && details.length && details[0]?.msg) ||
                        err?.response?.data?.msg ||
                        err?.response?.data?.message ||
                        'Failed to rename territory';

                      setErrorMessage(backendMsg);
                      setErrorDialogOpen(true);
                    } finally {
                      setRenaming(false);
                    }
                  }}
                  variant="contained"
                  disabled={!renameValue.trim() || renaming}
                >
                  {i18next.t('common:buttons.Save', { defaultValue: 'Save' })}
                </Button>

              </DialogActions>
            </Dialog>

            {/* CUSTOM TOAST */}
            <CustomToast
              open={toast.open}
              message={toast.msg}
              severity={toast.severity}
              onClose={() => setToast((p) => ({ ...p, open: false }))}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default TerritoryDetail;
